/**
 * Scene operations (spec 021): a small, fixed set of named, schema-described,
 * pure changes to a scene document, plus a text summary of a scene and a
 * read of one sprite (`getSprite`). Shared by
 * the editor store (`applyOperations`) and any adapter (the scene assistant,
 * spec 022). Transport-free: no Zustand, React, HTTP or model code.
 */
import {
  addSprite, editSprite, placeSprite, setSpriteMotion, setDialog, setSound, setBackground, setSceneTransitions, setSceneLength,
  setChoices, removeElement, renameScene, resetSpriteOrientation, setLineExpression,
} from './operations';
import { moveSprite, setSpriteState, showSprite, hideSprite, flipSprite } from './keyframeOperations';
import { setBackgroundStates, setBackgroundState } from './backgroundOperations';
import { validateAgainstSchema } from './schema';
import { SCENE_PICTURE_OPERATIONS } from './scenePictures';
import type { OperationContext, OperationError, OperationResult, OperationStep, SceneDocument, SceneOperation } from './types';

/**
 * The registry, in a fixed order (adapters build cached tool lists from it).
 * Adding, removing or reordering an entry is a spec change (AC-1 snapshot).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each entry has its own Args
export const SCENE_OPERATIONS: readonly SceneOperation<any>[] = Object.freeze([
  addSprite,
  editSprite,
  placeSprite,
  setSpriteMotion,
  setDialog,
  setSound,
  setBackground,
  setSceneTransitions,
  setSceneLength,
  setChoices,
  removeElement,
  renameScene,
  resetSpriteOrientation,
  setLineExpression,
  moveSprite,
  setSpriteState,
  showSprite,
  hideSprite,
  flipSprite,
  setBackgroundStates,
  setBackgroundState,
]);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BY_NAME: ReadonlyMap<string, SceneOperation<any>> = new Map(SCENE_OPERATIONS.map((op) => [op.name, op]));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PICTURE_BY_NAME: ReadonlyMap<string, SceneOperation<any>> = new Map(SCENE_PICTURE_OPERATIONS.map((op) => [op.name, op]));

/** The operation called `name`, or undefined. */
export function getOperation(name: string): SceneOperation<unknown> | undefined {
  return BY_NAME.get(name);
}

/**
 * Look up `name` in `SCENE_OPERATIONS`, then in spec 030's
 * `SCENE_PICTURE_OPERATIONS`, validate `args` against its `inputSchema`
 * (`invalid_args` with a `path`), then `apply`. No scene is refused for its
 * mode (D-1): a scene plays its keyframes, and a stale mode field
 * Storyboard left on a document is ignored. Unknown names fail with
 * `unknown_operation`. Never throws for bad input; `ctx.newId` throwing is
 * reported as `replay_mismatch`.
 */
export function runOperation(name: string, doc: SceneDocument, args: unknown, ctx: OperationContext): OperationResult {
  const op = BY_NAME.get(name) ?? PICTURE_BY_NAME.get(name);
  if (!op) return { ok: false, errors: [{ code: 'unknown_operation', message: `no operation ${name}` }] };
  const errors = validateAgainstSchema(op.inputSchema, args);
  if (errors.length) return { ok: false, errors };
  try {
    return op.apply(doc, args, ctx);
  } catch (err) {
    if (err instanceof ReplayMismatch) return { ok: false, errors: [{ code: 'replay_mismatch', message: err.message }] };
    throw err;
  }
}

/** Thrown by `replayContext().newId` when an operation asks for more ids than were recorded. */
export class ReplayMismatch extends Error {}

/**
 * A context whose `newId()` hands out `createdIds` in order (replay). After
 * the run, `leftover()` is the number of recorded ids not taken.
 */
export function replayContext(
  createdIds: readonly string[],
  media?: OperationContext['media'],
  imageSizes?: OperationContext['imageSizes'],
  mediaLengths?: OperationContext['mediaLengths'],
): OperationContext & { leftover(): number } {
  let i = 0;
  return {
    media,
    ...(imageSizes ? { imageSizes } : {}),
    ...(mediaLengths ? { mediaLengths } : {}),
    newId() {
      if (i >= createdIds.length) throw new ReplayMismatch('the step asked for more new ids than it recorded');
      return createdIds[i++];
    },
    leftover: () => createdIds.length - i,
  };
}

export type ReplayResult =
  | { ok: true; doc: SceneDocument; summaries: string[] }
  | { ok: false; failedIndex: number; errors: OperationError[] };

/**
 * Replay recorded steps in order against `doc`, each step's `newId()`
 * returning its `createdIds`. All succeed or the result names the first
 * failing step; `doc` itself is never changed. The editor store's
 * `applyOperations` is this plus one undo step.
 */
export function replaySteps(
  doc: SceneDocument,
  steps: readonly OperationStep[],
  media?: OperationContext['media'],
  imageSizes?: OperationContext['imageSizes'],
  mediaLengths?: OperationContext['mediaLengths'],
): ReplayResult {
  let current = doc;
  const summaries: string[] = [];
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const ctx = replayContext(step.createdIds ?? [], media, imageSizes, mediaLengths);
    const result = runOperation(step.name, current, step.args, ctx);
    if (result.ok === false) return { ok: false, failedIndex: i, errors: result.errors };
    if (ctx.leftover() !== 0) {
      return { ok: false, failedIndex: i, errors: [{ code: 'replay_mismatch', message: 'the step recorded ids it did not use' }] };
    }
    current = result.doc;
    summaries.push(result.summary);
  }
  return { ok: true, doc: current, summaries };
}

export { describeScene, summaryOrientation, SUMMARY_NAME_CHARS } from './describeScene';
export {
  getSprite, SPRITE_COVERAGE, SPRITE_STATE_COVERAGE, ORIENTATION_LAYOUT_COVERAGE, RESPONSIVE_LAYOUT_COVERAGE, TRANSFORM_COVERAGE,
  HINGE_COVERAGE, SPRITE_SIZE_COVERAGE, KEYFRAME_COVERAGE, findSprite,
} from './getSprite';
export type { GetSpriteResult, GetSpriteErrorCode, SpriteView, SpriteOrientationView, FieldCoverage, SpriteLookup } from './getSprite';
export { describeMotion, motionText, MOTION_PHRASES_MAX, type MotionDescription } from './describeMotion';
export { KEY_PROPERTIES, keyViews, storedProperty, keyPropertyName, type KeyProperty, type KeyView } from './keyNames';
export { moveSprite, setSpriteState, showSprite, hideSprite, flipSprite, KEY_RANGES } from './keyframeOperations';
export type { MoveSpriteArgs, SetSpriteStateArgs, ShowHideSpriteArgs } from './keyframeOperations';
export { setBackgroundStates, setBackgroundState, backgroundTransitionText } from './backgroundOperations';
export type { SetBackgroundStatesArgs, SetBackgroundStateArgs } from './backgroundOperations';
export { resolvePlacement, derivePosition, nearestAtLabel, backToFront, depthZIndices, AT_X, VERTICAL_Y, GAP_X, POSITION_RANGE } from './placement';
export { validateAgainstSchema, schemaKeywordErrors } from './schema';
export { PLAYBACK_ARG_SCHEMA, playbackFromArg, playbackToArg, type PlaybackArg } from './playbackArg';
export { EASE_TO_GSAP, EASES, RANGES, secToMs } from './units';
export {
  addSprite, editSprite, placeSprite, setSpriteMotion, setDialog, setSound, setBackground, setSceneTransitions, setSceneLength,
  setChoices, removeElement, renameScene, resetSpriteOrientation, setLineExpression, documentPlayLength, documentContentEnd,
  isHeldScene,
  motionKeyframeId, DEFAULT_TRANSITION_MS, BACKGROUND_NAME_MAX,
} from './operations';
export type {
  AddSpriteArgs, EditSpriteArgs, PlaceSpriteArgs, SetSpriteMotionArgs, SetDialogArgs, SetSoundArgs, SetBackgroundArgs,
  SetSceneTransitionsArgs, SetChoicesArgs, RemoveElementArgs, RenameSceneArgs, ResetSpriteOrientationArgs, ResetPart,
  SetLineExpressionArgs, StateArg,
} from './operations';
export type {
  SceneDocument, SceneOperation, OperationResult, OperationContext, OperationError, OperationErrorCode, OperationMedia,
  OperationStep, NewIdKind, JsonSchema, Placement, Motion, Ease, AtLabel, SceneTransitionArg,
} from './types';
export {
  SCENE_PICTURE_KEY, SCENE_PICTURE_OPERATIONS, SCENE_AGENT_PICTURE_TOOLS, scenePictureMapOf, scenePictureSceneOf, pictureStoryOf,
  scenePictureOperation, describeScenePictures,
} from './scenePictures';
export {
  describeChange, describeStoryChange, PLAIN_CHANGE_SCENE_OPERATIONS, PLAIN_CHANGE_STORY_OPERATIONS, PLAIN_CHANGE_SCENE_PICTURE_OPERATIONS,
} from './plainChange';
export type { PlainChange } from './plainChange';
